import { useCallback, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import { Popover, PopoverHeader, PopoverBody, Button } from 'reactstrap';
import { normalizeTreeStructure, getItemsIDs } from 'common/components/tree-layout/utils/helpers';

import SvgRender from 'common/components/general/SvgRender';
import burger from 'common/assets/svg/common/chapter.svg';
import lineRibbon from 'common/assets/svg/common/ribbon-line.svg';
import arrow from 'common/assets/svg/common/arrow.svg';
import dropdownArrowIcon from 'common/assets/svg/common/arrow.svg';
import clearIcon from 'common/assets/svg/actions/select-close.svg';
import priorityArrow from 'common/assets/svg/jobs/priority_arrow.svg';
import lock from 'common/assets/svg/common/lock.svg';
import Loader from 'common/components/tree-layout/components/Loader';
import { Input } from 'reactstrap';

import { getInitialSingleValue } from 'common/utils/lists';
import variables from 'common/assets/scss/abstracts/_exports.module.scss';
import useFilterSelector from 'common/components/filters/useFilterSelector';
import useTooltipID from 'common/utils/hooks/useTooltipID';
import { useUpdateEffect } from 'utils/hooks';

export const TreeSelectController = ({
  initialHeader = '',
  invisible,
  placeholder,
  value,
  onChange,
  list
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const { avoidRender, tooltipID } = useTooltipID('select-group-popup');

  useFilterSelector(value, { list }, { selected: selectedGroups, setSelected: setSelectedGroups });

  if (avoidRender) return null;

  return (
    <div className="position-relative">
      <Input
        id={tooltipID}
        value={
          value && selectedGroups[0]
            ? `${selectedGroups[0].code ? `${selectedGroups[0].code}. ` : ''}${selectedGroups[0].name}`
            : ''
        }
        placeholder={placeholder}
        invisible={invisible}
      />

      <Actions>
        {value && selectedGroups?.[0] ? (
          <SvgRender
            onClick={() => {
              setSelectedGroups([]);
              onChange([]);
            }}
            className="clear-indicator"
            src={clearIcon}
            style={{ width: 14, height: 14 }}
          />
        ) : null}
        <SvgRender
          onClick={() => setIsOpen(prev => !prev)}
          src={dropdownArrowIcon}
          style={{
            width: 10,
            height: 10,
            transform: isOpen ? `rotate(180deg)` : ''
          }}
        />
      </Actions>

      <TreeSelect
        fetchOptions={params => getInitialSingleValue('/store-groups', params)}
        initialFetchOptionsParams={
          selectedGroups?.[0]?.parent_id
            ? { parent_id: selectedGroups[0].parent_id }
            : { is_root: true }
        }
        initialHeader={initialHeader}
        target={tooltipID}
        isOpen={isOpen}
        toggle={() => setIsOpen(prev => !prev)}
        onSelect={option => {
          setSelectedGroups([option]);
          onChange([option.id]);
        }}
        initialSelectedOptionId={selectedGroups?.[0]?.id}
      />
    </div>
  );
};

const Actions = styled.div`
  position: absolute;
  right: 0.5rem;
  top: 5px;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 14px;

  .clear-indicator {
    color: #ccc;
    cursor: pointer;

    &:hover {
      color: ${variables.red};
    }
  }
`;

TreeSelectController.propTypes = {
  initialHeader: PropTypes.string,
  invisible: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  list: PropTypes.string.isRequired
};

const TreeSelect = ({
  target,
  className = '',
  isOpen,
  toggle,
  placement = 'bottom',
  headerClassName = '',
  bodyClassName = '',
  initialHeader = '',
  fetchOptions,
  initialFetchOptionsParams = {},
  onSelect,
  initialSelectedOptionId = null
}) => {
  const [treeOptions, setTreeOptions] = useState({});
  const [treeSubOptions, setTreeSubOptions] = useState({});
  const [currentParentId, setCurrentParentId] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(initialSelectedOptionId);
  const [isLoading, setIsLoading] = useState(false);

  const fetchInitialOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedOptions = await fetchOptions(initialFetchOptionsParams);
      const parentId = fetchedOptions[0]?.parent_id || 'root';

      setTreeOptions(normalizeTreeStructure(fetchedOptions));
      setTreeSubOptions({ [parentId]: getItemsIDs(fetchedOptions) });
      setCurrentParentId(parentId);
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }, [fetchOptions, initialFetchOptionsParams]);

  const fetchParentOptions = async id => {
    setIsLoading(true);

    try {
      let fetchParams = {};

      if (currentParentId !== 'root') {
        const parentObj = await fetchOptions({ id: id });

        if (parentObj[0].parent_id) {
          fetchParams = { parent_id: parentObj[0].parent_id };
        } else if (currentParentId === parentObj[0].id) {
          fetchParams = { is_root: true };
        } else {
          fetchParams = { parent_id: parentObj[0].id };
        }
      }

      if (fetchParams?.is_root && treeSubOptions?.root?.length) {
        setCurrentParentId('root');
        setIsLoading(false);

        return;
      } else if (fetchParams?.parent_id && treeSubOptions[fetchParams.parent_id]?.length) {
        setCurrentParentId(fetchParams.parent_id);
        setIsLoading(false);

        return;
      }

      const fetchedOptions = await fetchOptions(fetchParams);

      setTreeOptions(prev => ({ ...prev, ...normalizeTreeStructure(fetchedOptions) }));
      setTreeSubOptions(prev => ({
        ...prev,
        [fetchParams?.parent_id || 'root']: getItemsIDs(fetchedOptions)
      }));
      setCurrentParentId(fetchParams?.parent_id || 'root');
    } catch (e) {
      console.error(e);
    }

    setIsLoading(false);
  };

  const fetchChildOptions = useCallback(
    async id => {
      if (treeSubOptions[id]) {
        setCurrentParentId(id);
        return;
      }

      setIsLoading(true);

      try {
        const fetchedOptions = await fetchOptions({ parent_id: id });

        setTreeOptions(prev => ({ ...prev, ...normalizeTreeStructure(fetchedOptions) }));
        setTreeSubOptions(prev => ({
          ...prev,
          [id]: getItemsIDs(fetchedOptions)
        }));
        setCurrentParentId(id);
      } catch (e) {
        console.error(e);
      }

      setIsLoading(false);
    },
    [fetchOptions, treeSubOptions]
  );

  useEffect(() => {
    fetchInitialOptions();
  }, [fetchInitialOptions]);

  useUpdateEffect(() => {
    if (!initialSelectedOptionId && selectedOptionId) {
      setSelectedOptionId(null);
    }
  }, [initialSelectedOptionId]);

  useEffect(() => {
    if (initialSelectedOptionId) {
      setSelectedOptionId(initialSelectedOptionId);
    }
  }, [initialSelectedOptionId]);

  return (
    <Popover
      placement={placement}
      isOpen={isOpen}
      target={target}
      popperClassName={`tree-select min-width-fit ${className}`}
      toggle={toggle}
      hideArrow
    >
      <PopoverHeader className={`fs-12 fw-bold cpy-12 text-primary bg-white ${headerClassName}`}>
        {currentParentId === 'root' ? (
          initialHeader
        ) : (
          <div
            className="d-flex align-items-center cursor-pointer"
            onClick={() => {
              fetchParentOptions(currentParentId);
            }}
          >
            <SvgRender
              src={priorityArrow}
              style={{ width: 12, height: 12, transform: 'rotate(270deg)' }}
              className="me-1"
            />
            {treeOptions[treeSubOptions[currentParentId]?.[0]]?.parent?.name}
          </div>
        )}
      </PopoverHeader>

      <PopoverBody className={`tree-select__body gray-scrollbar text-nowrap p-0 ${bodyClassName}`}>
        {!isLoading ? (
          <>
            {treeSubOptions[currentParentId]?.length
              ? treeSubOptions[currentParentId].map(id => {
                  const option = treeOptions[id];
                  return (
                    <div
                      key={id}
                      id={id}
                      className={`d-flex align-items-center cursor-pointer fs-14 cpy-4 ps-1 ${
                        selectedOptionId === id
                          ? 'bg-primary text-white active'
                          : 'bg-white text-primary'
                      }`}
                      onClick={() => setSelectedOptionId(id)}
                    >
                      <SvgRender
                        src={
                          currentParentId === 'root'
                            ? option.is_extra_charges
                              ? lock
                              : burger
                            : lineRibbon
                        }
                        style={{ width: 14, height: 16 }}
                        className={`me-1 ${option.is_extra_charges ? 'text-violet' : ''}`}
                      />

                      <span>
                        {option.code ? `${option.code}. ` : ''}
                        {option.name}
                      </span>

                      <div
                        onClick={() => option?.descendants_count && fetchChildOptions(id)}
                        className={`next-arrow d-flex align-items-center justify-content-center py-1 ms-auto me-2 ${
                          !option?.descendants_count ? 'opacity-0' : ''
                        }`}
                      >
                        <SvgRender
                          src={arrow}
                          style={{ width: 12, height: 12, transform: 'rotate(270deg)' }}
                        />
                      </div>
                    </div>
                  );
                })
              : null}
          </>
        ) : (
          <Loader />
        )}
      </PopoverBody>

      <div className="tree-select__footer d-flex justify-content-end cpy-12 cpe-12 border-top">
        <Button color="cancel" className="fs-12 cpy-4 cpx-12" onClick={toggle}>
          CANCEL
        </Button>
        <Button
          onClick={() => {
            onSelect(treeOptions[selectedOptionId]);
            toggle();
          }}
          disabled={!selectedOptionId || !treeOptions[selectedOptionId]}
          color="primary"
          className="fs-12 cpy-4 cpx-12"
        >
          SELECT
        </Button>
      </div>
    </Popover>
  );
};

// To make this component work properly we need to have a specific response that looks like this:
// [
//   { id: 5, parent_id: 10, parent: { id: 28, name: 32, ... }, ... },
//   { id: 6, parent_id: 11, parent: { id: 29, name: 33, ... }, ... }
// ];

// also we need to be able to fetch with 'parent_id' param and with 'id' param in order to get the options
// based on the parent_id and also to get a single option so we can fetch his parent.

// When fetching with parent_id the response must be like the one above and when fetching with id
// it should look like this:

// [
//   { id: 7, parent_id: 12, parent: { id: 30, name: 34, ... }, ... }
// ];

TreeSelect.propTypes = {
  target: PropTypes.string, //it must be a tooltipID from the useTooltipID hook
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func, //to toggle the isOpen boolean
  placement: PropTypes.string, //the placement of the popup, it obviously has some specific values, you may check the reactstrap popup documentation
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  initialHeader: PropTypes.string, //this is the popup's header when we are on root level, when we go down the tree the header becomes the selected option
  fetchOptions: PropTypes.func, //the fetching function to fetch the options for each level
  initialFetchOptionsParams: PropTypes.object, //The params for the initial fetching of options
  onSelect: PropTypes.func, //this is the function which is called when an option is selected and it returns the selected option
  initialSelectedOptionId: PropTypes.number //The id of the option that you want to have selected by default
};

export default TreeSelect;
